import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../design-system/theme';
import { Template, ConsentForm } from '../../types/admin';
import {
  FormGroup as SharedFormGroup,
  Label as SharedLabel,
  Input as SharedInput,
  Textarea as SharedTextarea,
  FormRow as SharedFormRow,
  Card,
  PrimaryButton,
  SecondaryButton,
  ModalContent as SharedModalContent,
  ModalHeader as SharedModalHeader,
  ModalBody as SharedModalBody,
  CloseButton,
} from '../../styles/adminShared';

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  height: calc(100vh - 200px);
  min-height: 600px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const EditorPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

const PreviewPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  height: fit-content;
`;

const PanelHeader = styled.div`
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

// Use shared FormGroup but with custom margin for this component
const FormGroup = styled(SharedFormGroup)`
  margin-bottom: 20px;
`;

// Use shared Label
const Label = SharedLabel;

// Use shared Input
const Input = SharedInput;

// Use shared Textarea as TextArea
const TextArea = SharedTextarea;

// Use shared FormRow with custom layout for this component
const FormRow = styled(SharedFormRow)`
  grid-template-columns: 1fr 1fr;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  background: #fafbfc;
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  color: #34495e;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TemplateGrid = styled.div`
  display: grid;
  gap: 12px;
`;

const TemplateCard = styled.div<{ $selected: boolean }>`
  padding: 16px;
  border: 2px solid ${props => props.$selected ? theme.primary : '#e0e0e0'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: ${theme.primary};
    background: ${props => props.$selected ? '#f0f4ff' : '#f8f9fa'};
  }
`;

const TemplateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
`;

const TemplateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
`;

const TemplateMeta = styled.div`
  font-size: 12px;
  color: #7f8c8d;
`;

const TemplatePreviewBtn = styled.button`
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  background: white;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${theme.primary};
  white-space: nowrap;
  
  &:hover {
    background: ${theme.primary};
    color: white;
    border-color: ${theme.primary};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const EvidenceGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const EvidenceOption = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: ${props => props.$checked ? '#e8f4f8' : '#f8f9fa'};
  border: 2px solid ${props => props.$checked ? theme.primary : '#e0e0e0'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  
  &:hover {
    border-color: ${theme.primary};
  }
  
  input {
    display: none;
  }
  
  span.icon {
    font-size: 16px;
  }
`;

const ActionBar = styled.div`
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

// Preview Components
const PreviewFrame = styled.div`
  border: 8px solid #1e293b;
  border-radius: 24px;
  overflow: hidden;
  background: #f8fafc;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  max-width: 380px;
  margin: 0 auto;
`;

const PreviewDevice = styled.div`
  padding: 8px 12px;
  background: #1e293b;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 60px;
    height: 4px;
    border-radius: 4px;
    background: #475569;
  }
`;

const PreviewContent = styled.div`
  min-height: 400px;
  padding: 24px;
  background: white;
`;

const WizardPreview = styled.div`
  .preview-progress {
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    margin-bottom: 24px;
    overflow: hidden;
    
    .fill {
      height: 100%;
      width: 33%;
      background: ${theme.gradientPrimary};
      border-radius: 2px;
    }
  }
  
  .preview-step-badge {
    display: inline-block;
    padding: 4px 12px;
    background: #e8f4f8;
    color: ${theme.primary};
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  
  .preview-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }
  
  .preview-subtitle {
    color: #64748b;
    font-size: 14px;
    margin-bottom: 24px;
  }
`;

const PreviewOrg = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  margin-bottom: 16px;
  
  .org-avatar {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: ${theme.gradientPrimary};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    font-weight: 600;
  }
  
  .org-info {
    flex: 1;
    
    strong {
      display: block;
      color: #1e293b;
      margin-bottom: 2px;
    }
    
    span {
      font-size: 12px;
      color: #64748b;
    }
  }
`;

const PreviewDocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const PreviewDocItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  
  .doc-number {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
  }
  
  .doc-title {
    flex: 1;
    font-weight: 500;
    color: #1e293b;
  }
  
  .doc-status {
    font-size: 12px;
    color: #64748b;
    padding: 4px 8px;
    background: #f1f5f9;
    border-radius: 4px;
  }
`;

const PreviewButton = styled.div`
  padding: 12px 24px;
  background: ${theme.gradientPrimary};
  color: white;
  text-align: center;
  border-radius: 8px;
  font-weight: 600;
`;

const EmptyPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  h3 {
    color: #1e293b;
    margin-bottom: 8px;
  }
`;

const TemplateContentModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(SharedModalContent)`
  max-width: 800px;
`;

const ModalHeader = styled(SharedModalHeader)`
  h3 {
    margin: 0;
    color: #1e293b;
  }
`;

const ModalBody = styled(SharedModalBody)`
  max-height: 70vh;
`;

const ContentPreview = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  
  h4 {
    margin: 0 0 12px 0;
    color: ${theme.primary};
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .content {
    font-size: 14px;
    line-height: 1.7;
    color: #334155;
    
    strong, b {
      color: #1e293b;
    }
  }
`;

const EVIDENCE_TYPES = [
  { id: 'SIGNATURE', label: 'Digital Signature', icon: '✍️' },
  { id: 'PHONE_OTP', label: 'Phone OTP', icon: '📱' },
  { id: 'EMAIL_OTP', label: 'Email OTP', icon: '📧' },
  { id: 'VIDEO', label: 'Video Recording', icon: '🎥' },
  { id: 'AUDIO', label: 'Audio Recording', icon: '🎙️' },
];

interface FormBuilderProps {
  templates: Template[];
  initialData?: Partial<ConsentForm>;
  onSave: (data: Partial<ConsentForm>) => void;
  onCancel: () => void;
  isEdit?: boolean;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
  templates,
  initialData,
  onSave,
  onCancel,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    formTitle: initialData?.formTitle || '',
    formDescription: initialData?.formDescription || '',
    templateIds: initialData?.templateIds || [] as string[],
    evidenceTypes: initialData?.evidenceTypes || ['SIGNATURE'] as string[],
    successRedirectUrl: initialData?.successRedirectUrl || '',
    failureRedirectUrl: initialData?.failureRedirectUrl || '',
  });
  
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [previewPage, setPreviewPage] = useState(0); // Track which page/template is being previewed

  // Reset form data when initialData changes (e.g., when editing a different form)
  useEffect(() => {
    setFormData({
      formTitle: initialData?.formTitle || '',
      formDescription: initialData?.formDescription || '',
      templateIds: initialData?.templateIds || [] as string[],
      evidenceTypes: initialData?.evidenceTypes || ['SIGNATURE'] as string[],
      successRedirectUrl: initialData?.successRedirectUrl || '',
      failureRedirectUrl: initialData?.failureRedirectUrl || '',
    });
    setPreviewPage(0);
  }, [initialData]);

  const selectedTemplates = useMemo(() => {
    return templates.filter(t => formData.templateIds.includes(t.templateId));
  }, [templates, formData.templateIds]);

  const toggleTemplate = (templateId: string) => {
    setFormData(prev => ({
      ...prev,
      templateIds: prev.templateIds.includes(templateId)
        ? prev.templateIds.filter(id => id !== templateId)
        : [...prev.templateIds, templateId],
    }));
    setPreviewPage(0); // Reset preview to first page when templates change
  };

  const toggleEvidence = (evidenceType: string) => {
    setFormData(prev => ({
      ...prev,
      evidenceTypes: prev.evidenceTypes.includes(evidenceType)
        ? prev.evidenceTypes.filter(e => e !== evidenceType)
        : [...prev.evidenceTypes, evidenceType],
    }));
  };

  const handleSave = () => {
    onSave({
      formTitle: formData.formTitle,
      formDescription: formData.formDescription,
      templateIds: formData.templateIds,
      evidenceTypes: formData.evidenceTypes,
      successRedirectUrl: formData.successRedirectUrl || undefined,
      failureRedirectUrl: formData.failureRedirectUrl || undefined,
      active: true,
    });
  };

  const canSave = formData.formTitle.trim() && formData.templateIds.length > 0 && formData.evidenceTypes.length > 0;

  // Get template content for preview
  const getTemplateContent = (template: Template): string => {
    // Use consentText if available (preferred), otherwise content field
    return template.consentText || template.content || '';
  };

  return (
    <>
      <Container>
        {/* Editor Panel */}
        <EditorPanel>
          <PanelHeader>
            <span>📝</span>
            {isEdit ? 'Edit Consent Form' : 'Create Consent Form'}
          </PanelHeader>
          <PanelBody>
            {/* Basic Info */}
            <Section>
              <SectionTitle>📋 Form Details</SectionTitle>
              <FormGroup>
                <Label>Form Title *</Label>
                <Input
                  type="text"
                  value={formData.formTitle}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, formTitle: e.target.value })}
                  placeholder="e.g., Patient Registration Consent"
                />
              </FormGroup>
              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.formDescription}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData({ ...formData, formDescription: e.target.value })}
                  placeholder="Brief description of what this consent form is for..."
                />
              </FormGroup>
            </Section>

            {/* Template Selection */}
            <Section>
              <SectionTitle>
                📄 Select Templates *
                <span style={{ fontWeight: 'normal', color: '#64748b', fontSize: '13px' }}>
                  ({formData.templateIds.length} selected)
                </span>
              </SectionTitle>
              {templates.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>
                  No active templates available. Create templates first.
                </div>
              ) : (
                <TemplateGrid>
                  {templates.map((template) => (
                    <TemplateCard
                      key={template.templateId}
                      $selected={formData.templateIds.includes(template.templateId)}
                      onClick={() => toggleTemplate(template.templateId)}
                    >
                      <TemplateHeader>
                        <Checkbox
                          type="checkbox"
                          checked={formData.templateIds.includes(template.templateId)}
                          onChange={() => {}}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                        <TemplateInfo>
                          <TemplateName>{template.name || template.templateName}</TemplateName>
                          <TemplateMeta>
                            {template.projectName || 'No project'} • v{template.version || '1'}
                          </TemplateMeta>
                        </TemplateInfo>
                        <TemplatePreviewBtn
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setPreviewTemplate(template);
                          }}
                        >
                          👁️ Preview
                        </TemplatePreviewBtn>
                      </TemplateHeader>
                      {template.description && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#64748b' }}>
                          {template.description.substring(0, 100)}
                          {template.description.length > 100 ? '...' : ''}
                        </div>
                      )}
                    </TemplateCard>
                  ))}
                </TemplateGrid>
              )}
            </Section>

            {/* Evidence Types */}
            <Section>
              <SectionTitle>🔐 Evidence Collection *</SectionTitle>
              <EvidenceGroup>
                {EVIDENCE_TYPES.map((evidence) => (
                  <EvidenceOption
                    key={evidence.id}
                    $checked={formData.evidenceTypes.includes(evidence.id)}
                  >
                    <input
                      type="checkbox"
                      checked={formData.evidenceTypes.includes(evidence.id)}
                      onChange={() => toggleEvidence(evidence.id)}
                    />
                    <span className="icon">{evidence.icon}</span>
                    <span>{evidence.label}</span>
                  </EvidenceOption>
                ))}
              </EvidenceGroup>
            </Section>

            {/* Redirect URLs */}
            <Section>
              <SectionTitle>↩️ Redirect URLs (Optional)</SectionTitle>
              <FormRow>
                <FormGroup>
                  <Label>Success Redirect URL</Label>
                  <Input
                    type="url"
                    value={formData.successRedirectUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, successRedirectUrl: e.target.value })}
                    placeholder="https://yoursite.com/success"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Failure Redirect URL</Label>
                  <Input
                    type="url"
                    value={formData.failureRedirectUrl}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, failureRedirectUrl: e.target.value })}
                    placeholder="https://yoursite.com/failed"
                  />
                </FormGroup>
              </FormRow>
            </Section>
          </PanelBody>
          <ActionBar>
            <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSave} disabled={!canSave}>
              {isEdit ? 'Save Changes' : 'Create Form'}
            </PrimaryButton>
          </ActionBar>
        </EditorPanel>

        {/* Preview Panel */}
        <PreviewPanel>
          <PanelHeader>
            <span>👁️</span>
            Data Owner View Preview
            {selectedTemplates.length > 1 && (
              <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#64748b' }}>
                Page {previewPage + 1} of {selectedTemplates.length + 1}
              </span>
            )}
          </PanelHeader>
          <PanelBody>
            {selectedTemplates.length > 0 ? (
              <PreviewFrame>
                <PreviewDevice />
                <PreviewContent>
                  <WizardPreview>
                    <div className="preview-progress">
                      <div className="fill" style={{ width: `${((previewPage + 1) / (selectedTemplates.length + 1)) * 100}%` }} />
                    </div>
                    <div className="preview-step-badge">Step {previewPage + 1} of {selectedTemplates.length + 1}</div>
                    
                    {previewPage === 0 ? (
                      // Overview page
                      <>
                        <h2 className="preview-title">
                          {formData.formTitle || 'Consent Form'}
                        </h2>
                        <p className="preview-subtitle">
                          {formData.formDescription || 'Please review the following documents and provide your consent.'}
                        </p>
                        
                        <PreviewOrg>
                          <div className="org-avatar">
                            {(selectedTemplates[0]?.projectName || 'O').charAt(0)}
                          </div>
                          <div className="org-info">
                            <strong>{selectedTemplates[0]?.projectName || 'Organization'}</strong>
                            <span>{selectedTemplates[0]?.dataCollectorName || 'Data Collector'}</span>
                          </div>
                        </PreviewOrg>
                        
                        <PreviewDocList>
                          {selectedTemplates.map((template, idx) => (
                            <PreviewDocItem key={template.templateId}>
                              <div className="doc-number">{idx + 1}</div>
                              <div className="doc-title">{template.name || template.templateName || 'Consent Document'}</div>
                              <div className="doc-status">Required</div>
                            </PreviewDocItem>
                          ))}
                        </PreviewDocList>
                        
                        <div style={{ marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
                          <strong>Verification Methods:</strong>{' '}
                          {formData.evidenceTypes.map(e => 
                            EVIDENCE_TYPES.find(ev => ev.id === e)?.label || e
                          ).join(', ')}
                        </div>
                      </>
                    ) : (
                      // Individual template page
                      <>
                        <h2 className="preview-title" style={{ fontSize: '16px' }}>
                          {selectedTemplates[previewPage - 1]?.name || selectedTemplates[previewPage - 1]?.templateName || 'Consent Document'}
                        </h2>
                        <div 
                          style={{ 
                            fontSize: '13px', 
                            lineHeight: '1.6', 
                            color: '#374151', 
                            maxHeight: '200px', 
                            overflow: 'auto',
                            padding: '12px',
                            background: '#f8fafc',
                            borderRadius: '8px',
                            marginBottom: '16px'
                          }}
                          dangerouslySetInnerHTML={{ 
                            __html: getTemplateContent(selectedTemplates[previewPage - 1]) || '<p style="color:#94a3b8">No content</p>'
                          }}
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                          <input type="checkbox" id="consent-check" style={{ width: '18px', height: '18px' }} />
                          <label htmlFor="consent-check" style={{ fontSize: '13px', color: '#374151' }}>
                            I confirm I have read / heard and understood the above
                          </label>
                        </div>
                      </>
                    )}
                    
                    {/* Navigation buttons */}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {previewPage > 0 && (
                        <PreviewButton 
                          style={{ background: '#e2e8f0', color: '#475569', flex: 1 }}
                          onClick={() => setPreviewPage(p => p - 1)}
                        >
                          ← Back
                        </PreviewButton>
                      )}
                      <PreviewButton 
                        style={{ flex: previewPage === 0 ? 1 : 2 }}
                        onClick={() => setPreviewPage(p => Math.min(p + 1, selectedTemplates.length))}
                      >
                        {previewPage === selectedTemplates.length ? 'Submit Consent' : 'Continue →'}
                      </PreviewButton>
                    </div>
                    
                    {/* Page indicator dots */}
                    {selectedTemplates.length > 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
                        {[...Array(selectedTemplates.length + 1)].map((_, idx) => (
                          <div 
                            key={idx}
                            onClick={() => setPreviewPage(idx)}
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: idx === previewPage ? theme.primary : '#e2e8f0',
                              cursor: 'pointer',
                              transition: 'background 0.2s'
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </WizardPreview>
                </PreviewContent>
              </PreviewFrame>
            ) : (
              <EmptyPreview>
                <div className="icon">📋</div>
                <h3>No Templates Selected</h3>
                <p>Select at least one template to see how the consent form will appear to data owners.</p>
              </EmptyPreview>
            )}
          </PanelBody>
        </PreviewPanel>
      </Container>

      {/* Template Content Modal */}
      {previewTemplate && (
        <TemplateContentModal onClick={() => setPreviewTemplate(null)}>
          <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <ModalHeader>
              <h3>📄 {previewTemplate.name || previewTemplate.templateName}</h3>
              <CloseButton onClick={() => setPreviewTemplate(null)}>&times;</CloseButton>
            </ModalHeader>
            <ModalBody>
              <ContentPreview>
                <h4>📋 Consent Content</h4>
                <div 
                  className="content"
                  dangerouslySetInnerHTML={{ 
                    __html: getTemplateContent(previewTemplate) || '<p style="color:#94a3b8">No consent content defined</p>'
                  }}
                />
              </ContentPreview>
              
              {previewTemplate.dataCategories && previewTemplate.dataCategories.length > 0 && (
                <ContentPreview>
                  <h4>📊 Data Categories</h4>
                  <div className="content">
                    {previewTemplate.dataCategories.join(', ')}
                  </div>
                </ContentPreview>
              )}
              
              {previewTemplate.purpose && (
                <ContentPreview>
                  <h4>🎯 Purpose</h4>
                  <div className="content">
                    {previewTemplate.purpose}
                  </div>
                </ContentPreview>
              )}
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <ContentPreview>
                  <h4>🏢 Project</h4>
                  <div className="content">{previewTemplate.projectName || 'Not specified'}</div>
                </ContentPreview>
                <ContentPreview>
                  <h4>👤 Data Collector</h4>
                  <div className="content">{previewTemplate.dataCollectorName || 'Not specified'}</div>
                </ContentPreview>
              </div>
            </ModalBody>
          </ModalContent>
        </TemplateContentModal>
      )}
    </>
  );
};

export default FormBuilder;
